import React from "react";
import { Text, plainText } from "../../atoms/text/text";
import type { Membership } from "./channel-lineup.types";
import { INDICATOR_GLYPH, MEMBERSHIPS } from "./indicator.shared";
import styles from "./indicator-legend.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * The ✓ / $ / ★ key — inside the add-on section's header bar (GS9) and above the merged search
 * results (GQ3). Server Component.
 */
export interface IndicatorLegendProps {
  /**
   * Which rows to list, in the order given. GQ3 shows only the indicator types present in the
   * results; GS9's header bar shows all three, which is the default.
   */
  memberships?: readonly Membership[];
  /** The ✓ line. Absent or blank, the line is left out. */
  includedLine?: string;
  /** The $ line. Absent or blank, the line is left out. */
  addonLine?: string;
  /** The ★ line. Absent or blank, the line is left out. */
  promoLine?: string;
  /** Layout only. */
  className?: string;
}

function IndicatorLegendImpl({
  memberships = MEMBERSHIPS,
  includedLine,
  addonLine,
  promoLine,
  className
}: IndicatorLegendProps) {
  const authored: Partial<Record<Membership, string>> = {
    included: includedLine,
    addon: addonLine,
    promo: promoLine
  };
  /* A line with no wording is left out: its badge is `aria-hidden`, so it would be a list item a
     screen reader announces with nothing in it. */
  const lines = memberships
    .map(membership => ({
      membership,
      text: plainText((authored[membership] ?? "").trim())
    }))
    .filter(line => line.text !== "");

  if (lines.length === 0) {
    return null;
  }

  return (
    <ul className={[styles.legend, className].filter(Boolean).join(" ")}>
      {lines.map(({ membership, text }) => {
        const glyph = INDICATOR_GLYPH[membership];
        return (
          <li className={styles.row} key={membership}>
            {/* The badge repeats what the wording beside it says, so it is
                hidden rather than named — a reader would otherwise hear the
                state twice per line. */}
            <span aria-hidden="true" className={styles.badge} data-membership={membership}>
              <svg className={styles.glyph} focusable="false" viewBox="0 0 16 16">
                {glyph.fill && <path d={glyph.fill} fill="currentColor" />}
                {(glyph.strokes ?? []).map(d => (
                  <path
                    d={d}
                    fill="none"
                    key={d}
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.75"
                  />
                ))}
              </svg>
            </span>
            <Text as="span" variant="body-3">
              {text}
            </Text>
          </li>
        );
      })}
    </ul>
  );
}
export const IndicatorLegend = withSafeRender(IndicatorLegendImpl, "IndicatorLegend");

export default IndicatorLegend;
